'use client';
import {
  PokerGameType6PlusHoldem,
  PokerGameTypeCad,
  PokerGameTypeEur,
  PokerGameTypeGbp,
  PokerGameTypeKo,
  PokerGameTypeMysteryBounty,
  PokerGameTypePro,
  PokerGameTypeProBounty,
  PokerGameTypeUsd,
  PokerGameTypeZoom,
  PokerSeats10,
  PokerSeats2,
  PokerSeats3,
  PokerSeats4,
  PokerSeats5,
  PokerSeats6,
  PokerSeats7,
  PokerSeats8,
  PokerSeats9,
  PokerSpeedHyperTurbo,
  PokerSpeedRegular,
  PokerSpeedSlow,
  PokerSpeedTurbo
} from '@ps/web-fusion-icons';
import type { MessageDescriptor } from 'react-intl';

import Flex from '../../Flex/Flex';
import Icon from '../../Icon/Icon';
import Popover, { type Props as PopoverProps } from '../../Popover/Popover';
import { useIntl } from '../../hooks';
import {
  TXTCLI_2_seat_table_XHeads_upX,
  TXTCLI_CurrTitle_CAD,
  TXTCLI_CurrTitle_EUR,
  TXTCLI_CurrTitle_GBP,
  TXTCLI_CurrTitle_USD,
  TXTCLI_Currencies,
  TXTCLI_Game_Speed,
  TXTCLI_Game_types,
  TXTCLI_HoldXem,
  TXTCLI_Hyper_Turbo,
  TXTCLI_Mystery_Bounty_Tournament,
  TXTCLI_N0_seat_table,
  TXTCLI_Progressive_Knockout,
  TXTCLI_Regular,
  TXTCLI_Slow,
  TXTCLI_Table_Size,
  TXTCLI_Team_PokerStars_Bounty,
  TXTCLI_Team_PokerStars_player,
  TXTCLI_Total_Progressive_Knockout,
  TXTCLI_Turbo,
  TXTCLI_Zoom
} from '../../translations';

import styles from './TournamentTicketPopover.module.scss';

interface CategoryItem {
  icon: React.FC<React.SVGProps<SVGElement>>;
  color: string;
  label: MessageDescriptor;
  labelParams?: Record<string, number | string>;
}

interface Category {
  title: MessageDescriptor;
  items: CategoryItem[];
}

const TABLE_SIZES: CategoryItem[] = [
  {
    icon: PokerSeats10,
    color: 'var(--snowflake-component-games-list-icon-color-purple)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 10 }
  },
  {
    icon: PokerSeats9,
    color: 'var(--snowflake-component-games-list-icon-color-grey)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 9 }
  },
  {
    icon: PokerSeats8,
    color: 'var(--snowflake-component-games-list-icon-color-light-blue)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 8 }
  },
  {
    icon: PokerSeats7,
    color: 'var(--snowflake-component-games-list-icon-color-teal)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 7 }
  },
  {
    icon: PokerSeats6,
    color: 'var(--snowflake-component-games-list-icon-color-green)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 6 }
  },
  {
    icon: PokerSeats5,
    color: 'var(--snowflake-component-games-list-icon-color-orange)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 5 }
  },
  {
    icon: PokerSeats4,
    color: 'var(--snowflake-component-games-list-icon-color-blue)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 4 }
  },
  {
    icon: PokerSeats3,
    color: 'var(--snowflake-component-games-list-icon-color-magenta)',
    label: TXTCLI_N0_seat_table,
    labelParams: { 0: 3 }
  },
  {
    icon: PokerSeats2,
    color: 'var(--snowflake-component-games-list-icon-color-red)',
    label: TXTCLI_2_seat_table_XHeads_upX
  }
];

const GAME_SPEEDS: CategoryItem[] = [
  {
    icon: PokerSpeedSlow,
    color: 'var(--snowflake-component-games-list-icon-color-blue)',
    label: TXTCLI_Slow
  },
  {
    icon: PokerSpeedRegular,
    color: 'var(--snowflake-component-games-list-icon-color-green)',
    label: TXTCLI_Regular
  },
  {
    icon: PokerSpeedTurbo,
    color: 'var(--snowflake-component-games-list-icon-color-orange)',
    label: TXTCLI_Turbo
  },
  {
    icon: PokerSpeedHyperTurbo,
    color: 'var(--snowflake-component-games-list-icon-color-red)',
    label: TXTCLI_Hyper_Turbo
  }
];

const CURRENCIES: CategoryItem[] = [
  {
    icon: PokerGameTypeEur,
    color: 'var(--snowflake-component-games-list-icon-color-blue)',
    label: TXTCLI_CurrTitle_EUR
  },
  {
    icon: PokerGameTypeGbp,
    color: 'var(--snowflake-component-games-list-icon-color-yellow)',
    label: TXTCLI_CurrTitle_GBP
  },
  {
    icon: PokerGameTypeUsd,
    color: 'var(--snowflake-component-games-list-icon-color-green)',
    label: TXTCLI_CurrTitle_USD
  },
  {
    icon: PokerGameTypeCad,
    color: 'var(--snowflake-component-games-list-icon-color-red)',
    label: TXTCLI_CurrTitle_CAD
  }
];

const GAME_TYPES: CategoryItem[] = [
  {
    icon: PokerGameTypeZoom,
    color: 'var(--snowflake-component-games-list-icon-color-brand-red)',
    label: TXTCLI_Zoom
  },
  {
    icon: PokerGameType6PlusHoldem,
    color: 'var(--snowflake-component-games-list-icon-color-yellow)',
    label: TXTCLI_HoldXem
  },
  {
    icon: PokerGameTypeMysteryBounty,
    color: 'var(--snowflake-component-games-list-icon-color-brand-red)',
    label: TXTCLI_Mystery_Bounty_Tournament
  },
  {
    icon: PokerGameTypeKo,
    color: 'var(--snowflake-component-games-list-icon-color-red)',
    label: TXTCLI_Progressive_Knockout
  },
  {
    icon: PokerGameTypeKo,
    color: 'var(--snowflake-component-games-list-icon-color-yellow)',
    label: TXTCLI_Total_Progressive_Knockout
  },
  {
    icon: PokerGameTypeProBounty,
    color: 'var(--snowflake-component-games-list-icon-color-brand-red)',
    label: TXTCLI_Team_PokerStars_Bounty
  },
  {
    icon: PokerGameTypePro,
    color: 'var(--snowflake-component-games-list-icon-color-brand-red)',
    label: TXTCLI_Team_PokerStars_player
  }
];

const CATEGORIES: Category[] = [
  { title: TXTCLI_Table_Size, items: TABLE_SIZES },
  { title: TXTCLI_Game_Speed, items: GAME_SPEEDS },
  { title: TXTCLI_Currencies, items: CURRENCIES },
  { title: TXTCLI_Game_types, items: GAME_TYPES }
];

function CategorySection({ title, items }: Category) {
  const { formatMessage } = useIntl();

  return (
    <div>
      <div className={styles.category}>{formatMessage(title)}</div>
      {items.map((item, index) => (
        <Flex key={index} gap="xs" alignItems="center">
          <Icon size="sm" color={item.color} icon={item.icon} />
          {formatMessage(item.label, item.labelParams)}
        </Flex>
      ))}
    </div>
  );
}

export type TournamentTicketPopoverProps = Omit<PopoverProps, 'children'> & {
  children: PopoverProps['children'][0];
};

export function TournamentTicketPopover({
  children,
  ...popoverProps
}: TournamentTicketPopoverProps) {
  return (
    <Popover {...popoverProps} className={styles.popover}>
      {children}
      <Flex className={styles.popoverContainer} direction={{ base: 'column', md: 'row' }} gap="xl">
        {CATEGORIES.map((category, index) => (
          <CategorySection key={index} title={category.title} items={category.items} />
        ))}
      </Flex>
    </Popover>
  );
}
export default TournamentTicketPopover;
